/**
 * Decimal maths on money STRINGS.
 *
 * Never Number(). A voucher of 0.1 + 0.2 must equal 0.3 exactly, and
 * JavaScript's binary floats cannot do that. These helpers work on integer
 * halalas (2dp) and hand back strings, so what the user typed is what the
 * server receives.
 */
const SCALE = 100n

function toMinor(s: string): bigint {
  const clean = (s ?? '').trim().replace(/,/g, '')
  if (!clean) return 0n
  const neg = clean.startsWith('-')
  const body = neg ? clean.slice(1) : clean
  const [whole = '0', frac = ''] = body.split('.')
  const cents = (frac + '00').slice(0, 2)
  const v = BigInt(whole || '0') * SCALE + BigInt(cents || '0')
  return neg ? -v : v
}

function fromMinor(v: bigint): string {
  const neg = v < 0n
  const abs = neg ? -v : v
  const whole = abs / SCALE
  const frac = (abs % SCALE).toString().padStart(2, '0')
  return `${neg ? '-' : ''}${whole}.${frac}`
}

export function addMoney(...values: string[]): string {
  return fromMinor(values.reduce((a, v) => a + toMinor(v), 0n))
}

export function subMoney(a: string, b: string): string {
  return fromMinor(toMinor(a) - toMinor(b))
}

/** amount * rate, rounded half-up to 2dp. Used for VAT. */
export function mulMoney(amount: string, rate: string): string {
  const a = toMinor(amount)
  const [rw = '0', rf = ''] = rate.split('.')
  const rateScale = BigInt(10) ** BigInt(rf.length || 0)
  const r = BigInt(rw + (rf || '')) || 0n
  const product = a * r
  const half = rateScale / 2n
  return fromMinor((product + (product >= 0n ? half : -half)) / rateScale)
}

export function isZero(s: string): boolean { return toMinor(s) === 0n }
export function isPositive(s: string): boolean { return toMinor(s) > 0n }
export function eqMoney(a: string, b: string): boolean {
  return toMinor(a) === toMinor(b)
}

/** Display with thousands separators; blank stays blank while typing. */
export function display(s: string): string {
  if (!s) return ''
  const v = fromMinor(toMinor(s))
  const [w, f] = v.split('.')
  return `${BigInt(w).toLocaleString('en-US')}.${f}`
}

/** Keep only what can belong in a money field. */
export function sanitize(input: string): string {
  const cleaned = input.replace(/[^\d.]/g, '')
  const parts = cleaned.split('.')
  if (parts.length <= 1) return cleaned
  return `${parts[0]}.${parts.slice(1).join('').slice(0, 2)}`
}

export function normalise(s: string): string {
  return s ? fromMinor(toMinor(s)) : ''
}

/**
 * A signed balance (debit +, credit -) shown the accountant's way:
 * a positive figure followed by Dr or Cr. Zero has no side.
 */
export function withSide(signed: string): { amount: string; side: 'Dr' | 'Cr' | '' } {
  const v = toMinor(signed)
  if (v === 0n) return { amount: display('0'), side: '' }
  return { amount: display(fromMinor(v < 0n ? -v : v)), side: v > 0n ? 'Dr' : 'Cr' }
}

/** A balance and its side as one signed string: Dr positive, Cr negative. */
export function signed(balance: string, side: 'D' | 'C'): string {
  return side === 'C' ? fromMinor(-toMinor(balance)) : normalise(balance)
}

/** "92,000.00 Cr" from a signed "-92000.00"; "0.00" for zero. */
export function sidedText(signedValue: string): string {
  const w = withSide(signedValue)
  return w.side ? `${w.amount} ${w.side}` : w.amount
}
